import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { InvoiceAction } from '../../domain/enums/invoice-action.enum';
import { Invoice } from '../../domain/entities/invoice.entity';
import { InvoiceSummary } from '../../domain/use-cases/get-invoice-summary.use-case';
import { InvoiceRevenueEditData } from '../../domain/models/invoice-revenue-edit.model';

export interface PendingInvoiceAction {
  invoiceId: number | string;
  action: InvoiceAction;
}

export interface InvoiceStoreState {
  loading: boolean;
  loadingMore: boolean;
  invoices: readonly Invoice[];
  total: number;
  summary: InvoiceSummary;
  errorMessage: string | null;
  pendingAction: PendingInvoiceAction | null;
}

const initialState: InvoiceStoreState = {
  loading: false,
  loadingMore: false,
  invoices: [],
  total: 0,
  summary: { issued: 0, errors: 0 },
  errorMessage: null,
  pendingAction: null,
};

@Injectable()
export class InvoiceStore {
  private readonly stateSubject = new BehaviorSubject<InvoiceStoreState>(initialState);
  readonly state$ = this.stateSubject.asObservable();

  setLoading(): void {
    this.patch({ loading: true, loadingMore: false, errorMessage: null });
  }

  setLoadingMore(): void {
    this.patch({ loadingMore: true, errorMessage: null });
  }

  setInvoices(invoices: readonly Invoice[], total: number): void {
    this.patch({ loading: false, loadingMore: false, invoices, total, errorMessage: null, pendingAction: null });
  }

  appendInvoices(invoices: readonly Invoice[], total: number): void {
    this.patch({
      loadingMore: false,
      invoices: [...this.stateSubject.value.invoices, ...invoices],
      total,
      errorMessage: null,
    });
  }

  setSummary(summary: InvoiceSummary): void {
    this.patch({ summary });
  }

  setError(errorMessage: string): void {
    this.patch({ loading: false, loadingMore: false, errorMessage });
  }

  setPendingAction(invoiceId: number | string, action: InvoiceAction): void {
    this.patch({ pendingAction: { invoiceId, action } });
  }

  clearPendingAction(): void {
    this.patch({ pendingAction: null });
  }

  updateRevenue(data: InvoiceRevenueEditData): void {
    this.patch({
      invoices: this.stateSubject.value.invoices.map((invoice) =>
        invoice.revenueId === data.revenueId
          ? { ...invoice, customer: data.customer, address: data.address }
          : invoice
      ),
      pendingAction: null,
      errorMessage: null,
    });
  }

  private patch(partial: Partial<InvoiceStoreState>): void {
    this.stateSubject.next({ ...this.stateSubject.value, ...partial });
  }
}
